import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { Bell } from "lucide-react";

import openSocket from "../../services/socket-io";
import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { subscribeToPushNotifications } from "../../utils/pushNotifications";

const NotificationsPopOver = () => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const ticketIdUrl = +history.location.pathname.split("/")[2];
  const ticketIdRef = useRef(ticketIdUrl);
  const userRef = useRef(user);
  const buttonRef = useRef();
  const panelRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [, setDesktopNotifications] = useState([]);
  const { tickets } = useTickets({ withUnreadMessages: "true" });
  const soundAlertRef = useRef();

  useEffect(() => {
    const audio = new Audio(alertSound);
    soundAlertRef.current = () => audio.play().catch(() => {});
    if ("Notification" in window) {
      Notification.requestPermission()
        .then((permission) => {
          if (permission === "granted") return subscribeToPushNotifications();
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => { setNotifications(tickets); }, [tickets]);
  useEffect(() => { ticketIdRef.current = ticketIdUrl; }, [ticketIdUrl]);
  useEffect(() => { userRef.current = user; }, [user]);

  useEffect(() => {
    const handler = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const socket = openSocket();

    const handleConnect = () => socket.emit("joinNotification");

    const handleTicket = (data) => {
      if (data.action === "updateUnread" || data.action === "delete") {
        setNotifications((prev) => prev.filter((t) => t.id !== data.ticketId));
        setDesktopNotifications((prev) => {
          const idx = prev.findIndex((n) => n.tag === String(data.ticketId));
          if (idx !== -1) {
            prev[idx].close();
            return prev.filter((_, i) => i !== idx);
          }
          return prev;
        });
      }
    };

    const handleAppMessage = (data) => {
      if (
        data.action === "create" &&
        !data.message.read &&
        (data.ticket.userId === userRef.current?.id || !data.ticket.userId)
      ) {
        setNotifications((prev) => {
          const idx = prev.findIndex((t) => t.id === data.ticket.id);
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = data.ticket;
            return next;
          }
          return [data.ticket, ...prev];
        });

        const skip =
          (data.message.ticketId === ticketIdRef.current &&
            document.visibilityState === "visible") ||
          (data.ticket.userId && data.ticket.userId !== userRef.current?.id);
        if (!skip) handleNotifications(data);
      }
    };

    socket.on("connect", handleConnect);
    socket.on("reconnect", handleConnect);
    socket.on("ticket", handleTicket);
    socket.on("appMessage", handleAppMessage);

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("reconnect", handleConnect);
      socket.off("ticket", handleTicket);
      socket.off("appMessage", handleAppMessage);
    };
  }, []);

  const handleNotifications = ({ message, contact, ticket }) => {
    const notification = new Notification(
      `${i18n.t("tickets.notification.message")} ${contact.name}`,
      {
        body: `${message.body} - ${format(new Date(), "HH:mm")}`,
        icon: contact.profilePicUrl,
        tag: ticket.id,
        renotify: true,
      }
    );
    notification.onclick = (e) => {
      e.preventDefault();
      window.focus();
      history.push(`/tickets/${ticket.id}`);
    };
    setDesktopNotifications((prev) => {
      const idx = prev.findIndex((n) => n.tag === notification.tag);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = notification;
        return next;
      }
      return [notification, ...prev];
    });
    soundAlertRef.current?.();
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen((v) => !v)}
        className="relative p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        aria-label="Notificaciones"
      >
        <Bell className="w-4 h-4" />
        {notifications.length > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 bg-indigo-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
            {notifications.length > 9 ? "9+" : notifications.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full mt-1.5 w-80 max-h-[360px] overflow-y-auto bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-lg z-50 animate-slide-up"
        >
          <div className="px-3 py-2.5 border-b border-slate-100 dark:border-slate-800">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Notificaciones
            </p>
          </div>
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400 gap-2">
              <Bell className="w-8 h-8 opacity-30" />
              <p className="text-sm">{i18n.t("notifications.noTickets")}</p>
            </div>
          ) : (
            notifications.map((ticket) => (
              <div key={ticket.id} onClick={() => setIsOpen(false)}>
                <TicketListItem ticket={ticket} />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsPopOver;
